import { useEffect, useState } from "react";
import { useApiFetch, useLanguage } from "../../hooks";
import { Edit } from "../edit";
import { API_URL } from "../../services";
import { passMinLength, validatePasswordStrength } from "../../utils";

enum EditEnum {
  PASSWORD = "password",
  CONFIRM_PASSWORD = "confirmPassword",
}

export interface EditProfileProps {
  edit: EditProps;
  setEdit: (newEdit: EditProps) => void;
  setDisabled: (state: boolean) => void;
}

export interface EditProps {
  [EditEnum.PASSWORD]: string;
  [EditEnum.CONFIRM_PASSWORD]: string;
}

export const initialEdit: EditProps = {
  password: "",
  confirmPassword: "",
};

interface MeProps {
  email: string;
  eans: string[];
  apiUsers: string[];
}

export const EditProfile: React.FC<EditProfileProps> = ({
  edit,
  setEdit,
  setDisabled,
}) => {
  const { t } = useLanguage();

  const [fetchMe, me] = useApiFetch<MeProps>({
    email: "",
    eans: [],
    apiUsers: [],
  });

  const [editOk, setEditOk] = useState<EditProps>(initialEdit);
  const [editErrors, setEditErrors] = useState<EditProps>(initialEdit);

  useEffect(() => {
    fetchMe(API_URL.ME(), { method: "GET" });
  }, []);

  const handleEdit = ({ key, value }: { key: EditEnum; value: string }) => {
    setEdit({
      ...edit,
      [key]: value,
    });
  };

  const handleBlur = ({ key, value }: { key: EditEnum; value: string }) => {
    const hasError = (): string => {
      switch (key) {
        case EditEnum.PASSWORD:
          return validatePassword(value);
        case EditEnum.CONFIRM_PASSWORD:
          return validatePassword(value, true);
        default:
          return "";
      }
    };

    setEditOk({
      ...editOk,
      [key]: hasError().length === 0,
    });
    setEditErrors({
      ...editErrors,
      [key]: hasError(),
    });
  };

  const validatePassword = (
    value: string,
    isConfirm: boolean = false
  ): string => {
    const strengthError = validatePasswordStrength(value, {
      minLengthPrefix: t.global.password.errors.minLength.prefix,
      minLengthSuffix: t.global.password.errors.minLength.suffix,
      minUppercase: t.global.password.errors.minAUppercase,
      minLowercase: t.global.password.errors.minALowercase,
      minNumber: t.global.password.errors.minANumber,
      minSpecial: t.global.password.errors.minASpecial,
    });

    if (isConfirm && value === edit[EditEnum.PASSWORD]) {
      return strengthError;
    }
    if (isConfirm) {
      return [strengthError, t.global.password.errors.mismatch]
        .filter(Boolean)
        .join("\r\n· ");
    }

    return strengthError;
  };

  useEffect(() => {
    if (
      editOk[EditEnum.PASSWORD] &&
      editOk[EditEnum.CONFIRM_PASSWORD] &&
      edit[EditEnum.PASSWORD].length > 0 &&
      edit[EditEnum.PASSWORD] === edit[EditEnum.CONFIRM_PASSWORD]
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [edit, editOk]);

  return (
    <>
      <Edit>
        <Edit.Divider text={t.global.editprofile.readonly.email} />
        <Edit.Input id="email" value={me.email} disabled />

        <Edit.Divider text={t.global.editprofile.readonly.eans} />
        <Edit.Input id="eans" value={me.eans.join(", ")} disabled />

        <Edit.Divider text={t.global.editprofile.readonly.apiUsers} />
        <Edit.Input id="apiUsers" value={me.apiUsers.join(", ")} disabled />

        <Edit.Divider text={t.global.editprofile.divider} />
        <Edit.Password
          id={EditEnum.PASSWORD}
          placeholder={t.global.editprofile.placeholders.password}
          onChange={(e) =>
            handleEdit({ key: e.target.id as EditEnum, value: e.target.value })
          }
          onBlur={(e) =>
            handleBlur({ key: e.target.id as EditEnum, value: e.target.value })
          }
          error={editErrors.password}
        />
        <Edit.Password
          id={EditEnum.CONFIRM_PASSWORD}
          placeholder={t.global.editprofile.placeholders.confirmPassword}
          onChange={(e) =>
            handleEdit({ key: e.target.id as EditEnum, value: e.target.value })
          }
          onBlur={(e) =>
            handleBlur({ key: e.target.id as EditEnum, value: e.target.value })
          }
          error={editErrors.confirmPassword}
          disabled={
            edit[EditEnum.PASSWORD].length < passMinLength ||
            editErrors[EditEnum.PASSWORD].length > 0
          }
        />
      </Edit>
    </>
  );
};
